import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const ToolIntroduction = () => {
  const features = [
    {
      icon: FiIcons.FiDroplet,
      title: "Color Picker",
      description: "Click the color square or paste any hex code to instantly analyze its psychological impact and brand potential."
    },
    {
      icon: FiIcons.FiActivity,
      title: "Real-Time Analysis",
      description: "Watch as AI decodes your color's emotional triggers, industry fit, and conversion potential in milliseconds."
    },
    {
      icon: FiIcons.FiTrendingUp,
      title: "Actionable Insights",
      description: "Get brutal, data-driven recommendations—not vague design theory. Know exactly what's working and what's killing conversions."
    },
    {
      icon: FiIcons.FiZap,
      title: "Instant Application",
      description: "Export CSS, share palettes, or apply colors to live mockups. Stop theorizing, start implementing."
    }
  ];

  const steps = [
    {
      number: "1",
      action: "Pick Your Color",
      detail: "Click the color square or paste a hex code"
    },
    {
      number: "2",
      action: "Analyze Impact",
      detail: "Review mood, accessibility, and risk scores"
    },
    {
      number: "3",
      action: "Explore Alternatives",
      detail: "Use swatches and industry guides to refine"
    },
    {
      number: "4",
      action: "Export & Apply",
      detail: "Generate code snippets and share results"
    }
  ];

  return (
    <div className="glass-panel p-6 sm:p-8 md:p-10 lg:p-12 rounded-2xl sm:rounded-3xl border border-blue-500/20 mb-6 sm:mb-8 md:mb-10 relative overflow-hidden">
      {/* Decorative gradient */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 blur-3xl rounded-full -mr-32 -mt-32 pointer-events-none" />
      
      <div className="relative z-10 space-y-6 sm:space-y-8 md:space-y-10">
        {/* Header */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="flex justify-center mb-3 sm:mb-4">
            <div className="w-12 h-1 bg-blue-500 rounded-full opacity-50" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black italic uppercase tracking-tighter text-white">
            Stop Guessing. Start Knowing.
          </h2>
          <p className="text-sm sm:text-base text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Most brands choose colors based on "what feels right." That's why 73% fail to differentiate. 
            This tool gives you the <strong className="text-blue-400">brutal truth</strong> about your palette—
            backed by psychology, accessibility standards, and conversion data.
          </p>
        </div>

        {/* Quick Start Steps */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="relative group"
            >
              <div className="p-4 sm:p-5 bg-white/[0.03] hover:bg-white/[0.06] rounded-xl border border-white/10 hover:border-blue-500/30 transition-all h-full">
                <div className="flex items-start gap-3 sm:gap-4 mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <span className="text-base sm:text-lg font-black text-blue-400">{step.number}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs sm:text-sm font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
                      {step.action}
                    </h4>
                    <p className="text-[10px] sm:text-xs text-gray-400 leading-relaxed">
                      {step.detail}
                    </p>
                  </div>
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-2 -translate-y-1/2 text-blue-500/30">
                  <SafeIcon icon={FiIcons.FiChevronRight} className="text-xl" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Key Features */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 pt-6 sm:pt-8 border-t border-white/10">
          {features.map((feature, idx) => (
            <div key={idx} className="flex gap-3 sm:gap-4 p-4 sm:p-5 bg-white/[0.02] rounded-xl border border-white/5 hover:border-blue-500/20 transition-all group">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                <SafeIcon icon={feature.icon} className="text-blue-400 text-lg sm:text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs sm:text-sm font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
                  {feature.title}
                </h4>
                <p className="text-[10px] sm:text-xs text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center pt-4 sm:pt-6">
          <p className="text-xs sm:text-sm text-blue-300/60 italic">
            👇 Start by picking a color below. Your first analysis takes less than 5 seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ToolIntroduction;